import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import Button from '@/components/UI/Button';
import EntityListSelect from '@/components/UI/EntityListSelect';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import FormSwitch from '@/components/UI/FormSwitch';
import SearchSelect from '@/components/UI/SearchSelect';
import SegmentedControl from '@/components/UI/SegmentedControl';
import Tooltip from '@/components/UI/Tooltip';
import { useEntityLists } from '@/hooks/useEntityLists';
import {
    alphaAtlasEntityKey,
    ACROSS_WINDOWS_META,
    ACROSS_WINDOWS_ORDER,
    ALPHA_ATLAS_WINDOW_ROLES,
    type AlphaAtlasAcrossWindows,
    type AlphaAtlasConditionKind,
    type AlphaAtlasDirection,
    type AlphaAtlasEvidenceState,
    type AlphaAtlasExposure,
    type AlphaAtlasFilterState,
    type AlphaAtlasReportFilters,
    alphaAtlasDirectionLabel,
    CONDITION_KIND_META,
    countActiveNarrowingFilters,
    EVIDENCE_STATE_FILTER_ORDER,
    EVIDENCE_STATE_META,
    isVerificationBearingWindow,
    WINDOW_ROLE_META
} from './AlphaAtlasReportState';
import { formatHorizon, formatMarketDate } from './alphaAtlasFormat';
import { catalogIndexByFingerprint, catalogSelectItems, entitySelectItems } from './alphaAtlasSelectItems';

type DirectionChoice = 'both' | AlphaAtlasDirection;

interface AlphaAtlasFiltersProps {
    filters: AlphaAtlasFilterState;
    options: AlphaAtlasReportFilters | undefined;
    optionsLoading: boolean;
    exposure: AlphaAtlasExposure | null;
    /** Applies a partial change; the owner resets the page to 1. */
    onChange: (patch: Partial<AlphaAtlasFilterState>) => void;
    onClearNarrowing: () => void;
}

const ControlGroup = ({ label, help, children }: { label: string; help?: ReactNode; children: ReactNode }) => (
    <div>
        <div className="mb-1 flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-zinc-500">
            {label}
            {help && (
                <Tooltip content={help}>
                    <FaInfoCircle aria-label={`About ${label.toLowerCase()}`} className="h-3 w-3 text-zinc-600 hover:text-zinc-400" />
                </Tooltip>
            )}
        </div>
        {children}
    </div>
);

const windowHelp = (
    <div className="max-w-xs space-y-1">
        {ALPHA_ATLAS_WINDOW_ROLES.map((role) => (
            <p key={role}>
                <strong>{WINDOW_ROLE_META[role].shortLabel}.</strong> {WINDOW_ROLE_META[role].description}
            </p>
        ))}
    </div>
);

/** Typing pause before a min-instances change is applied, so each digit does not fire a query. */
const MIN_SUPPORT_COMMIT_DELAY_MS = 600;

const AlphaAtlasFilters = ({ filters, options, optionsLoading, exposure, onChange, onClearNarrowing }: AlphaAtlasFiltersProps) => {
    const [minSupportDraft, setMinSupportDraft] = useState<number | null>(filters.minSupport ?? null);
    const minSupportTimer = useRef<number | null>(null);
    useEffect(() => setMinSupportDraft(filters.minSupport ?? null), [filters.minSupport]);
    useEffect(
        () => () => {
            if (minSupportTimer.current !== null) window.clearTimeout(minSupportTimer.current);
        },
        []
    );
    const commitMinSupport = (value: number | null) => {
        if (minSupportTimer.current !== null) window.clearTimeout(minSupportTimer.current);
        minSupportTimer.current = null;
        const next = value !== null && value > 0 ? value : undefined;
        if (next !== filters.minSupport) onChange({ minSupport: next });
    };
    const handleMinSupportChange = (value: number | null) => {
        setMinSupportDraft(value);
        if (minSupportTimer.current !== null) window.clearTimeout(minSupportTimer.current);
        minSupportTimer.current = window.setTimeout(() => commitMinSupport(value), MIN_SUPPORT_COMMIT_DELAY_MS);
    };

    const parents = useMemo(() => options?.parents ?? [], [options]);
    const children = useMemo(() => options?.children ?? [], [options]);
    const parentItems = useMemo(() => catalogSelectItems(parents), [parents]);
    const childItems = useMemo(() => catalogSelectItems(children), [children]);
    const parentIndexByFingerprint = useMemo(() => catalogIndexByFingerprint(parents), [parents]);
    const childIndexByFingerprint = useMemo(() => catalogIndexByFingerprint(children), [children]);
    const entityItems = useMemo(() => entitySelectItems(options?.entities ?? []), [options]);
    const atlasEntityKeys = useMemo(() => new Set((options?.entities ?? []).map(alphaAtlasEntityKey)), [options]);
    const { entityLists } = useEntityLists();
    const [entityListNote, setEntityListNote] = useState<string | null>(null);
    /** Adds a saved entity list's members that exist in the Alpha Atlas universe to the current selection. */
    const addEntityList = (entityListId: number | null) => {
        if (entityListId === null) return;
        const entityList = entityLists.find((candidate) => candidate.id === entityListId);
        if (!entityList) throw new Error(`Entity list ${entityListId} is not loaded`);
        const inUniverse = entityList.entities.map((entity) => `${entity.type}:${entity.id}`).filter((key) => atlasEntityKeys.has(key));
        const current = filters.scope === 'entity' ? filters.entityKeys : [];
        const merged = [...new Set([...current, ...inUniverse])];
        const excludedCount = entityList.entities.length - inUniverse.length;
        const excludedNote = excludedCount > 0 ? ' (the rest are not in the Alpha Atlas universe).' : '.';
        setEntityListNote(
            inUniverse.length === 0
                ? `None of the ${entityList.entities.length} entities in "${entityList.name}" are in the Alpha Atlas universe.`
                : `Added ${inUniverse.length} of ${entityList.entities.length} entities from "${entityList.name}"${excludedNote}`
        );
        if (merged.length > 0) onChange({ scope: 'entity', entityKeys: merged });
    };
    const horizons = options?.horizons ?? [];
    const selectedHorizon = horizons.find((horizon) => horizon.horizonSeconds === filters.horizonSeconds);
    const narrowingCount = countActiveNarrowingFilters(filters);

    return (
        <section aria-label="Alpha Atlas filters" className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
            <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
                <ControlGroup help={windowHelp} label="Evidence window">
                    <SegmentedControl
                        options={ALPHA_ATLAS_WINDOW_ROLES.map((role) => {
                            const locked = !exposure && isVerificationBearingWindow(role);
                            return {
                                value: role,
                                label: WINDOW_ROLE_META[role].shortLabel,
                                disabled: locked,
                                title: locked
                                    ? 'Locked until you reveal the full history of a hypothesis'
                                    : WINDOW_ROLE_META[role].description
                            };
                        })}
                        value={filters.windowRole}
                        onChange={(windowRole) => onChange({ windowRole })}
                    />
                    <p className="mt-1 text-xs text-zinc-500">
                        {exposure
                            ? 'Revealed windows include the verification interval and are exploratory only.'
                            : 'Verification, whole range, and recent year unlock after you reveal a hypothesis.'}
                    </p>
                </ControlGroup>
                <ControlGroup label="Outcome scenario">
                    <SegmentedControl<DirectionChoice>
                        ariaLabel="Outcome scenario"
                        options={[
                            { value: 'both', label: 'Both' },
                            { value: 'long', label: alphaAtlasDirectionLabel(filters.outcomeKind, 'long') },
                            { value: 'short', label: alphaAtlasDirectionLabel(filters.outcomeKind, 'short') }
                        ]}
                        value={filters.direction ?? 'both'}
                        onChange={(direction) => onChange({ direction: direction === 'both' ? undefined : direction })}
                    />
                </ControlGroup>
                <ControlGroup
                    help="How long after the decision the outcome is measured. Results are only counted once the horizon has matured."
                    label="Horizon"
                >
                    <SegmentedControl
                        disabled={optionsLoading}
                        options={[
                            { value: 'all', label: 'All' },
                            ...horizons.map((horizon) => ({
                                value: String(horizon.horizonSeconds),
                                label: formatHorizon(horizon.horizonSeconds)
                            }))
                        ]}
                        value={filters.horizonSeconds === undefined ? 'all' : String(filters.horizonSeconds)}
                        onChange={(value) => onChange({ horizonSeconds: value === 'all' ? undefined : Number(value) })}
                    />
                    {selectedHorizon && (
                        <p className="mt-1 text-xs text-zinc-500">
                            Mature through {formatMarketDate(selectedHorizon.matureDecisionEndAtUnix)}
                        </p>
                    )}
                </ControlGroup>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <div className="space-y-2">
                    <SearchSelect
                        noMargin
                        isLoading={optionsLoading}
                        items={entityItems}
                        label="Entities"
                        mode="multi"
                        placeholder="All monitored entities"
                        size="sm"
                        value={
                            filters.scope === 'entity'
                                ? (options?.entities ?? []).flatMap((entity, index) =>
                                      filters.entityKeys.includes(alphaAtlasEntityKey(entity)) ? [index + 1] : []
                                  )
                                : []
                        }
                        onChange={(indices) => {
                            const entityKeys = indices.flatMap((index) => {
                                const entity = options?.entities[index - 1];
                                return entity ? [alphaAtlasEntityKey(entity)] : [];
                            });
                            setEntityListNote(null);
                            onChange(entityKeys.length === 0 ? { scope: 'pooled', entityKeys: [] } : { scope: 'entity', entityKeys });
                        }}
                    />
                    <EntityListSelect
                        noMargin
                        aria-label="Add the entities of a saved entity list"
                        placeholder="Add from an entity list…"
                        size="sm"
                        value={null}
                        onChange={addEntityList}
                    />
                    {entityListNote && <p className="text-xs text-zinc-500">{entityListNote}</p>}
                    {filters.scope === 'entity' && filters.entityKeys.length >= 2 && (
                        <div className="flex items-center gap-2">
                            <FormSwitch
                                noMargin
                                checked={filters.bundleEntities}
                                disabled={isVerificationBearingWindow(filters.windowRole)}
                                label="Bundle selected entities"
                                onChange={(event) => onChange({ bundleEntities: event.target.checked })}
                            />
                            <Tooltip
                                content={
                                    isVerificationBearingWindow(filters.windowRole)
                                        ? 'Bundles are computed for the controlled windows only; switch to Historical or Selection validation.'
                                        : 'Show one aggregated result per hypothesis across the selected entities (re-computed from the atomic facts, including lift and the walk-forward twin) instead of one row per entity. Takes a few seconds.'
                                }
                            >
                                <FaInfoCircle aria-label="About bundling" className="h-3 w-3 text-zinc-600 hover:text-zinc-400" />
                            </Tooltip>
                        </div>
                    )}
                </div>
                <SearchSelect
                    noMargin
                    isLoading={optionsLoading}
                    items={parentItems}
                    label="Trigger"
                    mode="single"
                    placeholder="Any trigger"
                    size="sm"
                    value={filters.parentFingerprint ? (parentIndexByFingerprint.get(filters.parentFingerprint) ?? null) : null}
                    onChange={(index) => onChange({ parentFingerprint: index === null ? undefined : parents[index - 1]?.fingerprint })}
                />
                <SearchSelect
                    noMargin
                    isLoading={optionsLoading}
                    items={childItems}
                    label="Context"
                    mode="single"
                    placeholder="Any context"
                    size="sm"
                    value={filters.childFingerprint ? (childIndexByFingerprint.get(filters.childFingerprint) ?? null) : null}
                    onChange={(index) => onChange({ childFingerprint: index === null ? undefined : children[index - 1]?.fingerprint })}
                />
                <FormSelect
                    noMargin
                    label="Hypothesis type"
                    options={[
                        { value: '', label: 'Any hypothesis' },
                        ...(Object.keys(CONDITION_KIND_META) as AlphaAtlasConditionKind[]).map((kind) => ({
                            value: kind,
                            label: CONDITION_KIND_META[kind].label
                        }))
                    ]}
                    size="sm"
                    value={filters.conditionKind ?? ''}
                    onChange={(event) =>
                        onChange({ conditionKind: (event.target.value || undefined) as AlphaAtlasConditionKind | undefined })
                    }
                />
                <FormSelect
                    noMargin
                    label="Evidence state"
                    options={[
                        { value: '', label: 'All evidence states' },
                        ...EVIDENCE_STATE_FILTER_ORDER.map((state) => ({ value: state, label: EVIDENCE_STATE_META[state].label }))
                    ]}
                    size="sm"
                    value={filters.evidenceState ?? ''}
                    onChange={(event) =>
                        onChange({ evidenceState: (event.target.value || undefined) as AlphaAtlasEvidenceState | undefined })
                    }
                />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-3">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="flex items-center gap-2">
                        <div className="w-36">
                            <FormNumberInput
                                integer
                                noMargin
                                label="Min instances"
                                min={0}
                                placeholder="Any"
                                value={minSupportDraft}
                                onBlur={() => commitMinSupport(minSupportDraft)}
                                onValueChange={handleMinSupportChange}
                            />
                        </div>
                        <Tooltip content="Only show results with at least this many measurable observations. Confidence and evidence use the report’s effective sample count; reports generated under the current policy group overlapping observations into horizon-sized time blocks.">
                            <FaInfoCircle aria-label="About min instances" className="mt-5 h-3 w-3 text-zinc-600 hover:text-zinc-400" />
                        </Tooltip>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-64">
                            <FormSelect
                                noMargin
                                label="Across windows"
                                options={ACROSS_WINDOWS_ORDER.map((value) => ({ value, label: ACROSS_WINDOWS_META[value].label }))}
                                size="sm"
                                value={filters.acrossWindows}
                                onChange={(event) => onChange({ acrossWindows: event.target.value as AlphaAtlasAcrossWindows })}
                            />
                        </div>
                        <Tooltip content={ACROSS_WINDOWS_META[filters.acrossWindows].description}>
                            <FaInfoCircle aria-label="About across windows" className="mt-5 h-3 w-3 text-zinc-600 hover:text-zinc-400" />
                        </Tooltip>
                    </div>
                    <div className="flex items-center gap-2 md:mt-5">
                        <FormSwitch
                            noMargin
                            checked={filters.hideInsufficientSupport}
                            label="Hide insufficient support"
                            onChange={(event) => onChange({ hideInsufficientSupport: event.target.checked })}
                        />
                        <Tooltip content="Rows that fail the minimum support, breadth, or independent-block rules. Their numbers are real but too noisy to rank on; most of the atlas is in this state.">
                            <FaInfoCircle aria-label="About insufficient support" className="h-3 w-3 text-zinc-600 hover:text-zinc-400" />
                        </Tooltip>
                    </div>
                </div>
                {narrowingCount > 0 && (
                    <Button size="sm" variant="outline-dark" onClick={onClearNarrowing}>
                        Clear {narrowingCount} {narrowingCount === 1 ? 'filter' : 'filters'}
                    </Button>
                )}
            </div>
        </section>
    );
};

export default AlphaAtlasFilters;
